import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { ContentCard, DetailsGrid, MediumTitle, TextCallout } from '@indigo-ag/component-module';
import { BackgroundCheckStepStatus } from '@indigo-ag/schema';
import i18next from 'i18next';
import React from 'react';

import { getCheckStepStatusVariant, getStepStatusTranslation } from '../BrazilGrowerFinance/utils/backgroundCheck.util';

const EMPTY_VALUE = '-';

const getStepStatusDisplay = (stepStatus: BackgroundCheckStepStatus | undefined | null) => {
  if (!stepStatus) return;

  const label = getStepStatusTranslation(stepStatus);
  const variant = getCheckStepStatusVariant(stepStatus);
  return <TextCallout label={label} variant={variant} />;
};

interface Props {
  backgroundCheckEmbargoStatus?: BackgroundCheckStepStatus | null;
  backgroundCheckForcedLaborStatus?: BackgroundCheckStepStatus | null;
  backgroundCheckNegativeDebtStatus?: BackgroundCheckStepStatus | null;
  coIssuerName?: string | null;
  contactAge?: number | null;
  contactCity?: string | null;
  contactName?: string | null;
  contactState?: string | null;
  currentRating?: number | null;
  guarantorName?: string | null;
  salesManager?: string | null;
  salesRep?: string | null;
  spouseName?: string | null;
  yearsExperiencePlanting?: number | null;
  yearsWithIndigo?: number | null;
  isGrowerProfile?: boolean;
}

export const CreditRiskAssessmentCard = ({
  backgroundCheckEmbargoStatus,
  backgroundCheckForcedLaborStatus,
  backgroundCheckNegativeDebtStatus,
  coIssuerName,
  contactAge,
  contactCity,
  contactName,
  contactState,
  currentRating,
  guarantorName,
  salesManager,
  salesRep,
  spouseName,
  yearsExperiencePlanting,
  yearsWithIndigo,
  isGrowerProfile
}: Props) => {
  const cityAndState = `${contactCity ? `${contactCity}, ` : ''}${contactState ?? ''}`;
  const gridItems = [
    { label: i18next.t('name'), value: contactName || EMPTY_VALUE },
    {
      label: i18next.t('creditRiskAssessment_age'),
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_age'),
      value: contactAge != null ? i18next.t('years', { numYears: contactAge }) : EMPTY_VALUE
    },
    { label: i18next.t('contactRoleSpouse'), value: spouseName || EMPTY_VALUE },
    { label: i18next.t('contactRoleGuarantor'), value: guarantorName || EMPTY_VALUE },
    { label: i18next.t('creditRiskAssessment_coIssuer'), value: coIssuerName || EMPTY_VALUE },
    { label: i18next.t('creditRiskAssessment_cityAndState'), value: cityAndState || EMPTY_VALUE },
    {
      label: i18next.t('creditRiskAssessment_yearsOfExperience'),
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_experiencePlanting'),
      value: yearsExperiencePlanting != null ? i18next.t('years', { numYears: yearsExperiencePlanting }) : EMPTY_VALUE
    },
    {
      label: i18next.t('creditRiskAssessment_yearsOfIndigoRelationship'),
      labelIcon: faInfoCircle,
      description: i18next.t('calculationsTooltip_indigoRelationship'),
      value: yearsWithIndigo != null ? i18next.t('years', { numYears: yearsWithIndigo }) : EMPTY_VALUE
    },
    { label: i18next.t('creditRiskAssessment_salesRep'), value: salesRep || EMPTY_VALUE },
    { label: i18next.t('creditRiskAssessment_salesManager'), value: salesManager || EMPTY_VALUE },
    {
      label: i18next.t('embargoInfraction'),
      value: () => getStepStatusDisplay(backgroundCheckEmbargoStatus) || EMPTY_VALUE
    },
    {
      label: i18next.t('negativeDebt'),
      value: () => getStepStatusDisplay(backgroundCheckNegativeDebtStatus) || EMPTY_VALUE
    },
    {
      label: i18next.t('laborSanctions'),
      value: () => getStepStatusDisplay(backgroundCheckForcedLaborStatus) || EMPTY_VALUE
    }
  ];

  if (!isGrowerProfile) {
    gridItems.unshift({
      label: i18next.t('creditRiskAssessment_currentRating'),
      value: () =>
        currentRating != null ? <MediumTitle>{currentRating.toLocaleString()}</MediumTitle> : <>{EMPTY_VALUE}</>
    });
  }

  return (
    <ContentCard titleText={i18next.t('creditRiskAssessment')}>
      <DetailsGrid columnWidth={200} items={gridItems} />
    </ContentCard>
  );
};
